import { DateTime } from 'luxon';
import { maxBy } from 'lodash';

import { isAssetVisitCancelled, isAssetVisitRejected } from '@nova/core';

import { ArrivalStatus } from './types';

import type { Maybe } from '@nova/core';
import type { Arrival, ArrivalEvent, ArrivalsDateGroup } from './types';

/** API dates arrive as ISO strings even where `@nova/core` types them as `Date`. */
export function toDateTime(value: Maybe<Date | string>): Maybe<DateTime> {
  if (!value) {
    return null;
  }

  const dateTime = value instanceof Date ? DateTime.fromJSDate(value) : DateTime.fromISO(value);
  return dateTime.isValid ? dateTime : null;
}

function getCreatedMillis(arrival: Arrival): number {
  return toDateTime(arrival.createDateTime)?.toMillis() ?? 0;
}

export function sortArrivalsNewestFirst(arrivals: Arrival[]): Arrival[] {
  return [...arrivals].sort((first, second) => getCreatedMillis(second) - getCreatedMillis(first));
}

/** Keeps the input order, so sorted arrivals give date groups in the same order. */
export function groupArrivalsByDate(
  arrivals: Arrival[],
  timezone: Maybe<string>,
  locale: string
): ArrivalsDateGroup[] {
  const groups: ArrivalsDateGroup[] = [];

  for (const arrival of arrivals) {
    const createdAt = toDateTime(arrival.createDateTime);
    const zonedCreatedAt = timezone ? createdAt?.setZone(timezone) : createdAt;
    const date = zonedCreatedAt?.setLocale(locale).toFormat('DDDD') ?? '';
    const lastGroup = groups.at(-1);

    if (lastGroup?.date === date) {
      lastGroup.arrivals.push(arrival);
    } else {
      groups.push({ date, arrivals: [arrival] });
    }
  }

  return groups;
}

export function getArrivalCompanyName(arrival: Arrival): string {
  return arrival.company?.name || arrival.companyHint || '';
}

export function getLatestArrivalEvent(arrival: Arrival): Maybe<ArrivalEvent> {
  return maxBy(arrival.assetVisitEvents ?? [], event =>
    toDateTime(event.createDateTime)?.toMillis()
  );
}

function getPlannedArrivalStatus(arrival: Arrival): ArrivalStatus {
  const arrivedAt = toDateTime(arrival.createDateTime)?.startOf('minute');
  const appointmentStart = toDateTime(arrival.appointment?.start)?.startOf('minute');

  if (!arrivedAt || !appointmentStart) {
    return ArrivalStatus.OnTime;
  }

  const minutesBeforeStart = appointmentStart.diff(arrivedAt, 'minutes').minutes;
  if (minutesBeforeStart > 0) {
    return ArrivalStatus.Early;
  }
  if (minutesBeforeStart < 0) {
    return ArrivalStatus.Late;
  }

  return ArrivalStatus.OnTime;
}

export function getArrivalStatus(arrival: Arrival): ArrivalStatus {
  if (arrival.isPlanned) {
    return getPlannedArrivalStatus(arrival);
  }

  if (isAssetVisitRejected(arrival) || isAssetVisitCancelled(arrival)) {
    return ArrivalStatus.Rejected;
  }

  if (arrival.appointmentId) {
    return ArrivalStatus.AppointmentCreated;
  }

  return ArrivalStatus.PendingUpdate;
}
